import React, { useState, useMemo, useEffect } from "react";
import {
  Skeleton, Input, Select, message, Form, Button,
  DatePicker, Spin, Modal as AntdModal, Table, Popover,
} from "antd";
import { FaTrashAlt, FaUserPlus } from "react-icons/fa";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isoWeek from "dayjs/plugin/isoWeek";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { Timesheetapi } from "../../middleware/interceptor";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isoWeek);

const { RangePicker } = DatePicker;
const STATUS_OPTS = [
  { value: "Open", label: "Open" },
  { value: "In Progress", label: "In Progress" },
  { value: "Completed", label: "Completed" },
];

export default function TeamsActivities() {
  const [countsLoading, setCountsLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [data, setData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [tempdata, setTempData] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteTeamName, setDeleteTeamName] = useState("");
  const [modalLoading, setModalLoading] = useState(false);

  const [totalCount, setTotalCount] = useState(0);
  const [inProgressCount, setInProgressCount] = useState(0);
  const [openCount, setOpenCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);

  const [searchText, setSearchText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedProject, setSelectedProject] = useState("All");
  const [selectedMember, setSelectedMember] = useState("All");

  const [projectsList, setProjectsList] = useState([]);
  const [membersList, setMembersList] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [activeMembersList, setActiveMembersList] = useState([]);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [endDateUpdatingId, setEndDateUpdatingId] = useState(null); // ← ADD THIS
  const [startDateUpdatingId, setStartDateUpdatingId] = useState(null); // ← ADD THIS
  const [estHrsUpdatingId, setEstHrsUpdatingId] = useState(null); // 

  const [taskNameConfirm, setTaskNameConfirm] = useState({ visible: false, record: null, value: "" });

  const [taskNameUpdatingId, setTaskNameUpdatingId] = useState(null); // ← ADD with other states

  const [editingCell, setEditingCell] = useState({ id: null, field: null });
  const [editingValue, setEditingValue] = useState("");

  const [addMemberPopoverId, setAddMemberPopoverId] = useState(null);
  const [newMemberIds, setNewMemberIds] = useState([]);
  const [addMemberLoadingId, setAddMemberLoadingId] = useState(null);

  const PRIORITY_OPTS = [
  { value: "Low", label: "Low" },
  { value: "Medium", label: "Medium" },
  { value: "High", label: "High" },
];


  const handleCellSave = async (record, field, value) => {
    setEditingCell({ id: null, field: null });

    // ── Task name → show confirmation popup instead of saving directly ──
    if (field === "task_name") {
      if (!value || value.trim() === "") {
        toast.error("Task name cannot be empty");
        return;
      }
      if (value.trim() === record.task_name) {
        return; // No change, skip
      }
      // Show confirmation modal
      setTaskNameConfirm({ visible: true, record, value: value.trim() });
      return;
    }

    // ── Validation: end_date must be >= start_date ──
    if (field === "end_date") {
      const startDate = record.start_date ? dayjs(record.start_date) : null;
      const selectedEnd = dayjs(value);
      if (startDate && selectedEnd.isBefore(startDate, "day")) {
        toast.error(
          `End date cannot be less than start date (${startDate.format("DD-MM-YYYY")}). Please select ${startDate.format("DD-MM-YYYY")} or later.`,
          { autoClose: 4000 }
        );
        return;
      }
    }

    // ── Optimistic update ──
    setTempData((prev) =>
      prev.map((t) =>
        t.Team_id === record.Team_id ? { ...t, [field]: value } : t
      )
    );

    const fieldNameMap = {
      start_date: "start_date",
      end_date: "end_date",
      estimated_hours: "estimated_hours",
      task_name: "task_name",
    };

    const backendField = fieldNameMap[field];
    if (!backendField) return;

    // ── Show loader based on field ──
    if (field === "start_date") setStartDateUpdatingId(record.Team_id);
    else if (field === "end_date") setEndDateUpdatingId(record.Team_id);
    else if (field === "estimated_hours") setEstHrsUpdatingId(record.Team_id);

    try {
      const res = await Timesheetapi.post("api/Tasks/UpdateTaskField", {
        item_id: record.Team_id,
        field_name: backendField,
        field_value: String(value),
      });

      if (res.data?.status === 1) {
        toast.success("Updated successfully");
      } else {
        toast.error(res.data?.message || "Update failed");
        setTempData((prev) =>
          prev.map((t) =>
            t.Team_id === record.Team_id ? { ...t, [field]: record[field] } : t
          )
        );
      }
    } catch (err) {
      toast.error("Update failed");
      setTempData((prev) =>
        prev.map((t) =>
          t.Team_id === record.Team_id ? { ...t, [field]: record[field] } : t
        )
      );
    } finally {
      if (field === "start_date") setStartDateUpdatingId(null);
      else if (field === "end_date") setEndDateUpdatingId(null);
      else if (field === "estimated_hours") setEstHrsUpdatingId(null);
    }
  };

  const confirmTaskNameUpdate = async () => {
    const { record, value } = taskNameConfirm;
    setTaskNameConfirm({ visible: false, record: null, value: "" });

    // Optimistic update
    setTempData((prev) =>
      prev.map((t) =>
        t.Team_id === record.Team_id ? { ...t, task_name: value } : t
      )
    );

    setTaskNameUpdatingId(record.Team_id);

    try {
      const res = await Timesheetapi.post("api/Tasks/UpdateTaskField", {
        item_id: record.Team_id,
        field_name: "task_name",
        field_value: value,
      });

      if (res.data?.status === 1) {
        toast.success("Task name updated successfully");
      } else {
        toast.error(res.data?.message || "Update failed");
        // Rollback
        setTempData((prev) =>
          prev.map((t) =>
            t.Team_id === record.Team_id ? { ...t, task_name: record.task_name } : t
          )
        );
      }
    } catch (err) {
      toast.error("Update failed");
      // Rollback
      setTempData((prev) =>
        prev.map((t) =>
          t.Team_id === record.Team_id ? { ...t, task_name: record.task_name } : t
        )
      );
    } finally {
      setTaskNameUpdatingId(null);
    }
  };

  const [pageSize, setPageSize] = useState(15);

  const [selectedTimePeriod, setSelectedTimePeriod] = useState("week");
  const [selectedDates, setSelectedDates] = useState([
    dayjs().startOf("isoWeek"),
    dayjs().endOf("isoWeek"),
  ]);
  const [sdate, setSdate] = useState(dayjs().startOf("isoWeek").format("YYYY-MM-DD"));
  const [edate, setEdate] = useState(dayjs().endOf("isoWeek").format("YYYY-MM-DD"));
  const [selectedWeekDates, setSelectedWeekDates] = useState([
    dayjs().startOf("isoWeek").toDate(),
    dayjs().endOf("isoWeek").toDate(),
  ]);

  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  // ── Options ──────────────────────────────────────────────────────────
  const PROJECT_OPTIONS = useMemo(() => {


    // const unique = [...new Set(tempdata.map((t) => t.project_name).filter((n) => n && n !== "-").map((n) => n.trim()))];


    // return [{ value: "All", label: "All" }, ...unique.map((p) => ({ value: p, label: p }))];

    const unique = [...new Set(
  tempdata
    .map((t) => t.project_name)
    .filter((n) => n && n !== "-")
    .map((n) => n.trim())
)];
return [
  { value: "All", label: "All" },
  ...unique.map((p) => ({ value: p, label: p }))
];
  }, [tempdata]);

  const MEMBER_OPTIONS = useMemo(() => {
    const unique = [...new Set(tempdata.flatMap((t) => (t.member_name || "").split(", ").map((n) => n.trim()).filter((n) => n && n !== "-")))];
    return [{ value: "All", label: "All" }, ...unique.map((m) => ({ value: m, label: m }))];
  }, [tempdata]);

  const MODAL_PROJECT_OPTIONS = useMemo(() => {
    const seen = new Set();
    return projectsList.filter((p) => { if (!p.Project_id || seen.has(p.Project_id)) return false; seen.add(p.Project_id); return true; })
      .map((p) => ({ value: p.Project_id, label: p.Project_name }));
  }, [projectsList]);

  const MODAL_MEMBER_OPTIONS = useMemo(() =>
    activeMembersList.map((m) => ({ value: m.employee_id, label: m.employee_name })),
    [activeMembersList]);

  const handleProjectChange = (projectId) => {
    const proj = projectsList.find((p) => p.Project_id === projectId);
    setSelectedClientId(proj?.Client_id ?? null);
  };

  // ── Fetch worked hours and merge into task list ───────────────────────
  const fetchWorkedHours = async (tasks) => {
    if (!tasks || !tasks.length) return;
    try {
      const itemIds = tasks.map((t) => t.Team_id);
      const res = await Timesheetapi.post(`api/Tasks/GetTaskWorkedHours`,{item_ids:itemIds  });

      if (res.data?.status === 1) {
        // Build map: item_id -> members[]
        const hoursMap = {};
        (res.data.data || []).forEach((d) => {
          hoursMap[d.item_id] = d.members; // [{ emp_name, worked_hrs }]
        });

        setTempData((prev) =>
          prev.map((t) => {
            const members = hoursMap[t.Team_id] ?? [];
            const memberNames = (t.member_name || "").split(", ").map((n) => n.trim()).filter(Boolean);

            // Convert "HH:mm" → decimal for balance calculation
            const hrsToDecimal = (hrsStr) => {
              if (!hrsStr) return 0;
              const parts = hrsStr.split(":");
              if (parts.length !== 2) return 0;
              return parseInt(parts[0]) + parseInt(parts[1]) / 60;
            };

            // Build per-member worked hrs in same order as member_name
            const memberWorkedHrs = memberNames.map((name) => {
              const found = members.find((m) => m.emp_name === name);
              return found ? found.worked_hrs : "00:00";
            });

            // Total worked decimal for balance
            const totalWorkedDecimal = memberWorkedHrs.reduce(
              (sum, h) => sum + hrsToDecimal(h), 0
            );

            return {
              ...t,
              member_worked_hrs: memberWorkedHrs,       // ["02:00", "00:00"]
              worked_hours: totalWorkedDecimal,
              balance_hours: (t.estimated_hours || 0) - totalWorkedDecimal,
            };
          })
        );
      }
    } catch (err) {
      console.error("Failed to fetch worked hours:", err);
    }
  };

  // ── Fetch team activities ─────────────────────────────────────────────
  const fetchTeamActivities = async () => {
    setCountsLoading(true);
    setTableLoading(true);
    try {
      const emp_id = localStorage.getItem("employee_id");
      const role_id = localStorage.getItem("emp_role_id");

      const response = await Timesheetapi.get(
        `api/Tasks/GetTeamActivities?emp_id=${emp_id}&role_id=${role_id}`
      );

      if (response.data?.status === 1) {
        const lists = (response.data.Task_list || []).map((task) => ({
          Team_id: task.Item_id,
          task_id: `TSK-${task.Item_id}`,

          ticket_id: (task.Ticket_id && task.Ticket_id.toString().trim() !== "")
            ? task.Ticket_id
            : task.Item_id?.toString() || "-",


          task_name: task.Item_Name || "-",
          project_name: task.Project_name || "-",
          client_name: task.Client_name || "-",
          member_name: task.Assigned_To || "-",
          assigned_by: task.Assigned_By || "-",
          start_date: task.Start_Date ? task.Start_Date.split("T")[0] : null,
          end_date: task.End_Date ? task.End_Date.split("T")[0] : null,
          status: task.Item_status || "Open",
          priority: task.Item_Priority || "Low",
          estimated_hours: parseFloat(task.Planned_hours) || 0,
          worked_hours: 0,
          member_worked_hrs: [],
          balance_hours: parseFloat(task.Planned_hours) || 0,
          billing_status: "Billable",
          description: task.Description || "",
          item_type: task.Item_type || "",
        }));

        setTempData(lists);
        setRawData(lists);

        // ✅ Fetch worked hours right after
        await fetchWorkedHours(lists);
      } else {
        toast.error(response.data?.message || "Failed to load team activities.");
      }
    } catch (err) {
      console.error("Failed to fetch team activities:", err);
      toast.error("Failed to load team activities.");
    } finally {
      setTableLoading(false);
      setCountsLoading(false);
    }
  };

  // ── Fetch projects & members ──────────────────────────────────────────
  const fetchProjectsAndMembers = async () => {
    try {
      const emp_id = localStorage.getItem("employee_id");
      const role_id = localStorage.getItem("emp_role_id");
      const res = await Timesheetapi.get(`api/Tasks/GetProjectsByTeamMembers?emp_id=${emp_id}&role_id=${role_id}`);
      if (res.data?.status === 1) {
        setProjectsList(res.data.projects || []);
        const allMembers = res.data.all_members || [];
        setMembersList(allMembers);
        if (allMembers.length > 0) {
          const emails = allMembers.map((m) => m.employee_email).filter(Boolean);
          try {
            const azureRes = await Timesheetapi.post("api/User/getAzureStatusBatch", { emails });
            if (azureRes.data?.success) {
              const statusMap = {};
              (azureRes.data.data || []).forEach((item) => { statusMap[item.email.toLowerCase()] = item.status; });
              setActiveMembersList(allMembers.filter((m) => statusMap[(m.employee_email || "").toLowerCase()] === "Active"));
            } else {
              setActiveMembersList(allMembers);
            }
          } catch {
            setActiveMembersList(allMembers);
          }
        }
      }
    } catch (err) {
      console.error("Failed to fetch projects/members:", err);
    }
  };

  useEffect(() => {
    fetchTeamActivities();
    fetchProjectsAndMembers();
  }, []);

  // ── Filters ───────────────────────────────────────────────────────────
  const applyFilters = () => {
    let filtered = [...tempdata];

    // if (selectedProject && selectedProject !== "All")
    //   filtered = filtered.filter((i) => i.project_name === selectedProject);

    if (selectedProject && selectedProject !== "All")
  filtered = filtered.filter(
    (i) => (i.project_name || "").trim() === selectedProject.trim()
  );


    if (selectedMember && selectedMember !== "All")
      filtered = filtered.filter((i) =>
        (i.member_name || "").split(", ").map((n) => n.trim()).includes(selectedMember)
      );



    // if (selectedTimePeriod === "week" && selectedWeekDates.length === 2) {
    //   const ws = dayjs(selectedWeekDates[0]).startOf("day");
    //   const we = dayjs(selectedWeekDates[1]).endOf("day");
    //   filtered = filtered.filter((i) => { const d = i.start_date ? dayjs(i.start_date) : null; return d && d.isSameOrAfter(ws) && d.isSameOrBefore(we); });
    // } else if (selectedTimePeriod === "day" && selectedDates.length === 2) {
    //   const ds = dayjs(selectedDates[0]).startOf("day");
    //   const de = dayjs(selectedDates[1]).endOf("day");
    //   filtered = filtered.filter((i) => { const d = i.start_date ? dayjs(i.start_date) : null; return d && d.isSameOrAfter(ds) && d.isSameOrBefore(de); });
    // } else if (selectedTimePeriod === "month" && selectedDates.length === 2) {
    //   const ms = dayjs(selectedDates[0]).startOf("month");
    //   const me = dayjs(selectedDates[1]).endOf("month");
    //   filtered = filtered.filter((i) => { const d = i.start_date ? dayjs(i.start_date) : null; return d && d.isSameOrAfter(ms) && d.isSameOrBefore(me); });
    // } else if (selectedTimePeriod === "year" && selectedDates.length === 2) {
    //   const ys = dayjs(selectedDates[0]).startOf("year");
    //   const ye = dayjs(selectedDates[1]).endOf("year");
    //   filtered = filtered.filter((i) => { const d = i.start_date ? dayjs(i.start_date) : null; return d && d.isSameOrAfter(ys) && d.isSameOrBefore(ye); });
    // }

    const filterByStartDate = (filterStart, filterEnd) => {
      filtered = filtered.filter((i) => {
        const taskStart = i.start_date ? dayjs(i.start_date).startOf("day") : null;
        if (!taskStart) return false;

        return taskStart.isSameOrAfter(filterStart) && taskStart.isSameOrBefore(filterEnd);
      });
    };

if (selectedTimePeriod === "week" && selectedWeekDates.length === 2) {
  filterByStartDate(
    dayjs(selectedWeekDates[0]).startOf("day"),
    dayjs(selectedWeekDates[1]).endOf("day")
  );
} else if (selectedTimePeriod === "day" && selectedDates.length === 2) {
  filterByStartDate(
    dayjs(selectedDates[0]).startOf("day"),
    dayjs(selectedDates[1]).endOf("day")
  );
} else if (selectedTimePeriod === "month" && selectedDates.length === 2) {
  filterByStartDate(
    dayjs(selectedDates[0]).startOf("month"),
    dayjs(selectedDates[1]).endOf("month")
  );
} else if (selectedTimePeriod === "year" && selectedDates.length === 2) {
  filterByStartDate(
    dayjs(selectedDates[0]).startOf("year"),
    dayjs(selectedDates[1]).endOf("year")
  );
}



    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      filtered = filtered.filter((i) =>
        (i.task_name || "").toLowerCase().includes(q) ||
        (i.task_id || "").toLowerCase().includes(q) ||
        (i.project_name || "").toLowerCase().includes(q) ||
        (i.member_name || "").toLowerCase().includes(q) ||
        (i.ticket_id || "").toLowerCase().includes(q) ||
        (i.assigned_by || "").toLowerCase().includes(q) ||
        (i.status || "").toLowerCase().includes(q)
      );
    }
    setRawData(filtered);
  };

  useEffect(() => { applyFilters(); }, [searchText, selectedProject, selectedMember, selectedTimePeriod, selectedDates, selectedWeekDates, tempdata]);
  useEffect(() => {
    setData(selectedStatus === "All" ? [...rawData] : rawData.filter((p) => (p.status || "").toLowerCase() === selectedStatus.toLowerCase()));
  }, [rawData, selectedStatus]);
  useEffect(() => {
    setTotalCount(rawData.length);
    setInProgressCount(rawData.filter((p) => p.status === "In Progress").length);
    setOpenCount(rawData.filter((p) => p.status === "Open").length);
    setCompletedCount(rawData.filter((p) => p.status === "Completed").length);
  }, [rawData]);

  const handleTimePeriodChange = (value) => {
    setSelectedTimePeriod(value);
    const today = dayjs();
    if (value === "day") { setSelectedDates([today, today]); setSdate(today.format("YYYY-MM-DD")); setEdate(today.format("YYYY-MM-DD")); }
    else if (value === "week") { const s = today.startOf("isoWeek"); const e = today.endOf("isoWeek"); setSelectedDates([s, e]); setSdate(s.format("YYYY-MM-DD")); setEdate(e.format("YYYY-MM-DD")); setSelectedWeekDates([s.toDate(), e.toDate()]); }
    else if (value === "month") { setSelectedDates([today.startOf("month"), today.endOf("month")]); setSdate(today.startOf("month").format("YYYY-MM-DD")); setEdate(today.endOf("month").format("YYYY-MM-DD")); }
    else if (value === "year") { setSelectedDates([today.startOf("year"), today.endOf("year")]); setSdate(today.startOf("year").format("YYYY-MM-DD")); setEdate(today.endOf("year").format("YYYY-MM-DD")); }
  };

  const handleWeekSelection = (dates_, inst) => {
    if (inst && typeof inst.close === "function") inst.close();
    if (dates_?.length > 0) {
      const sel = dates_[0]; sel.setHours(0, 0, 0, 0);
      const start = new Date(sel); const dow = start.getDay();
      start.setDate(start.getDate() - (dow === 0 ? 6 : dow - 1)); start.setHours(0, 0, 0, 0);
      const end = new Date(start); end.setDate(start.getDate() + 6); end.setHours(0, 0, 0, 0);
      setSelectedWeekDates([start, end]); setSdate(dayjs(start).format("YYYY-MM-DD")); setEdate(dayjs(end).format("YYYY-MM-DD")); setSelectedDates([dayjs(start), dayjs(end)]);
    }
  };

  const handleDateChange = (dates) => {
    if (dates?.length === 2) { setSelectedDates(dates); setSdate(dates[0].startOf(selectedTimePeriod).format("YYYY-MM-DD")); setEdate(dates[1].endOf(selectedTimePeriod).format("YYYY-MM-DD")); }
  };

  // ── Resolve current member names (as shown on the row) back to employee IDs ──
  const getMemberIdsFromNames = (memberNameStr) => {
    const names = (memberNameStr || "").split(",").map((n) => n.trim()).filter(Boolean);
    return names
      .map((name) => membersList.find((m) => (m.employee_name || "").trim() === name)?.employee_id)
      .filter((id) => id != null);
  };

  // ── Add member(s) to an existing task row (reuses api/Projects/UpdateTaskMembers) ──
  const handleAddMember = async (record) => {
    if (!newMemberIds.length) return;

    const currentIds = getMemberIdsFromNames(record.member_name);
    const updatedIds = [...new Set([...currentIds, ...newMemberIds.map(Number)])];

    setAddMemberLoadingId(record.Team_id);
    try {
      const res = await Timesheetapi.post("api/Projects/UpdateTaskMembers", {
        Item_Id: record.Team_id,
        Member_Ids: updatedIds,
      });

      if (res.data?.Status === 1) {
        const addedNames = newMemberIds
          .map((id) => membersList.find((m) => m.employee_id === Number(id))?.employee_name)
          .filter(Boolean);

        setTempData((prev) =>
          prev.map((t) => {
            if (t.Team_id !== record.Team_id) return t;
            const existingNames = t.member_name && t.member_name !== "-"
              ? t.member_name.split(",").map((n) => n.trim()).filter(Boolean)
              : [];
            return { ...t, member_name: [...existingNames, ...addedNames].join(", ") };
          })
        );

        toast.success("Member added successfully");
        setAddMemberPopoverId(null);
        setNewMemberIds([]);
      } else {
        toast.error(res.data?.Msg || "Failed to add member");
      }
    } catch (err) {
      console.error("Failed to add member:", err);
      toast.error("Error adding member");
    } finally {
      setAddMemberLoadingId(null);
    }
  };

  const openDeleteModal = (record) => { setDeleteId(record.Team_id); setDeleteTeamName(record.task_name); setDeleteModal(true); };

  const confirmDelete = async () => {
    setModalLoading(true); setDeleteLoading(true);
    try {
      const res = await Timesheetapi.post("api/Tasks/DeleteTask", { item_id: deleteId });
      if (res.data?.status === 1) {
        toast.success("Task deleted successfully");
        setTempData((prev) => prev.filter((t) => t.Team_id !== deleteId));
        setDeleteModal(false); setDeleteId(null); setDeleteTeamName("");
      } else { toast.error(res.data?.message || "Failed to delete task"); }
    } catch (err) { console.error("Delete failed:", err); toast.error("Error deleting task"); }
    finally { setModalLoading(false); setDeleteLoading(false); }
  };

  const handleSubmit = async (values) => {
    setModalLoading(true);
    try {
      const payload = {
        ticket_id: values.ticket_id || "",
        task_name: values.task_name,
        project_id: values.project_id ?? null,
        client_id: selectedClientId ?? null,
        assigned_by: parseInt(localStorage.getItem("employee_id")),
        assigned_to: JSON.stringify(values.member_ids || []),
        estimated_hours: values.estimated_hours || 0, status: values.status,
        billing_status: values.billing_status,
        priority: values.priority || "Low",
        start_date: values.start_date ? dayjs(values.start_date).format("YYYY-MM-DD") : null,
        end_date: values.end_date ? dayjs(values.end_date).format("YYYY-MM-DD") : null,
        description: "",
      };
      const res = await Timesheetapi.post("api/Tasks/CreateTask", payload);
      if (res.data?.status === 1) {
        toast.success("Task created successfully");
        setIsModalOpen(false); setSelectedClientId(null); form.resetFields();
        fetchTeamActivities();
      } else { toast.error(res.data?.message || "Failed to create task"); }
    } catch (err) { console.error(err); toast.error("Error creating task"); }
    finally { setModalLoading(false); }
  };

  // ── Columns ───────────────────────────────────────────────────────────
  const columns = useMemo(() => [
    {
      title: "#", key: "index", width: 55, align: "center",
      render: (_, __, index) => <span className="text-[12px] text-gray-400">{index + 1}</span>,
    },
    {
      title: "Ticket ID", dataIndex: "ticket_id", key: "ticket_id", width: 100, fixed: "left",
      render: (val) => <span className="text-[12px] text-gray-500 font-[500]">{val || "-"}</span>,
    },
    {
      title: "Projects", dataIndex: "project_name", key: "project_name", width: 150,
      render: (val) => <span className="text-[13px] text-gray-700">{val || "-"}</span>,
    },
    {
      title: "Task Name", dataIndex: "task_name", key: "task_name", width: 220,
      render: (val, record) => {
        const isEditing = editingCell.id === record.Team_id && editingCell.field === "task_name";
        if (isEditing) {
          return (
            <Input
              autoFocus
              size="small"
              defaultValue={val}
              onBlur={(e) => handleCellSave(record, "task_name", e.target.value)}
              onPressEnter={(e) => handleCellSave(record, "task_name", e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          );
        }
        return (
          <span
            className="text-[13px] text-gray-800 font-[500] cursor-pointer hover:text-blue-600 hover:underline"
            onClick={() => setEditingCell({ id: record.Team_id, field: "task_name" })}
            title="Click to edit"
          >
            {val || "-"}
          </span>
        );
      },
    },
    {
      title: "Member Name", dataIndex: "member_name", key: "member_name", width: 190,
      render: (val, record) => {
        const members = (val || "").split(", ").map((n) => n.trim()).filter(Boolean);
        const currentIds = getMemberIdsFromNames(val);
        const availableToAdd = activeMembersList
          .filter((m) => !currentIds.includes(m.employee_id))
          .map((m) => ({ value: m.employee_id, label: m.employee_name }));

        return (
          <div className="flex items-start justify-between gap-1">
            <div>
              {members.length
                ? members.map((name, i) => (
                  <div key={i}>
                    <span className="text-[12px] text-gray-700">{name}</span>
                    {i < members.length - 1 && (
                      <div style={{ borderBottom: "1px dashed #e0e0e0", margin: "3px 0" }} />
                    )}
                  </div>
                ))
                : <span className="text-[13px] text-gray-700">-</span>}
            </div>
            <Popover
  trigger="click"
  placement="right"
  open={addMemberPopoverId === record.Team_id}
  onOpenChange={(open) => {
    setAddMemberPopoverId(open ? record.Team_id : null);
    setNewMemberIds([]);
  }}
  title={
    <div className="flex items-center justify-between gap-2">
      <span>Add Member</span>
      <Button
        type="primary"
        size="small"
        loading={addMemberLoadingId === record.Team_id}
        disabled={!newMemberIds.length}
        onClick={() => handleAddMember(record)}
      >
        Add
      </Button>
    </div>
  }
  content={
    <div style={{ width: 220 }}>
      <Select
        mode="multiple"
        showSearch
        allowClear
        placeholder="Select member(s)"
        style={{ width: "100%" }}
        optionFilterProp="label"
        options={availableToAdd}
        value={newMemberIds}
        onChange={setNewMemberIds}
      />
    </div>
  }
>
  <FaUserPlus
    className="text-[13px] text-blue-500 cursor-pointer mt-[2px] shrink-0"
    title="Add member"
  />
</Popover>
          </div>
        );
      },
    },

    {
      title: "Start Date", dataIndex: "start_date", key: "start_date", width: 130,
      render: (val, record) => {
        const isEditing = editingCell.id === record.Team_id && editingCell.field === "start_date";
        if (isEditing) {
          return (
            <DatePicker
              autoFocus
              open
              size="small"
              defaultValue={val ? dayjs(val) : null}
              format="DD-MM-YYYY"
              className="w-full"
              onChange={(date) => {
                if (date) handleCellSave(record, "start_date", date.format("YYYY-MM-DD"));
              }}
              onOpenChange={(open) => {
                // Only close editing if picker itself closed without selection
                if (!open) {
                  setEditingCell({ id: null, field: null });
                }
              }}
            />
          );
        }
        return (
          <span
            className="text-[13px] text-gray-700 cursor-pointer hover:text-blue-600 hover:underline"
            onClick={() => setEditingCell({ id: record.Team_id, field: "start_date" })}
            title="Click to edit"
          >
            {val ? dayjs(val).format("DD-MM-YYYY") : "-"}
          </span>
        );
      },
    },
    {
      title: "End Date", dataIndex: "end_date", key: "end_date", width: 130,
      render: (val, record) => {
        const isEditing = editingCell.id === record.Team_id && editingCell.field === "end_date";
        if (isEditing) {
          return (
            <DatePicker
              autoFocus
              open
              size="small"
              defaultValue={val ? dayjs(val) : null}
              format="DD-MM-YYYY"
              className="w-full"
              disabledDate={(current) => {
                const start = record.start_date ? dayjs(record.start_date) : null;
                return start ? current && current.isBefore(start, "day") : false;
              }}
              onChange={(date) => {
                if (date) handleCellSave(record, "end_date", date.format("YYYY-MM-DD"));
              }}
              onOpenChange={(open) => {
                // Only close editing if picker itself closed without selection
                if (!open) {
                  setEditingCell({ id: null, field: null });
                }
              }}
            />
          );
        }
        return (
          <span
            className="text-[13px] text-gray-700 cursor-pointer hover:text-blue-600 hover:underline"
            onClick={() => setEditingCell({ id: record.Team_id, field: "end_date" })}
            title="Click to edit"
          >
            {val ? dayjs(val).format("DD-MM-YYYY") : "-"}
          </span>
        );
      },
    },


    {
      title: "Status", dataIndex: "status", key: "status", width: 150,
      render: (val, record) => {
        const handleStatusChange = async (newStatus) => {
          setStatusUpdatingId(record.Team_id);
          try {
            const res = await Timesheetapi.post("api/Tasks/UpdateTaskStatus", { item_id: record.Team_id, status: newStatus });
            if (res.data?.status === 1) {
              toast.success("Status updated successfully");
              setTempData((prev) => prev.map((t) => t.Team_id === record.Team_id ? { ...t, status: newStatus } : t));
            } else { toast.error(res.data?.message || "Failed to update status"); }
          } catch (err) { console.error(err); toast.error("Error updating status"); }
          finally { setStatusUpdatingId(null); }
        };
        return <Select value={val} size="small" style={{ width: 130, height: "30px" }} options={STATUS_OPTS} onChange={handleStatusChange} />;
      },
    },

    {
  title: "Priority", dataIndex: "priority", key: "priority", width: 130,
  render: (val, record) => {
    const handlePriorityChange = async (newPriority) => {
      setStatusUpdatingId(record.Team_id); // reuse same loader
      try {
        const res = await Timesheetapi.post("api/Tasks/UpdateTaskField", {
          item_id: record.Team_id,
          field_name: "priority",
          field_value: newPriority,
        });
        if (res.data?.status === 1) {
          toast.success("Priority updated successfully");
          setTempData((prev) =>
            prev.map((t) =>
              t.Team_id === record.Team_id ? { ...t, priority: newPriority } : t
            )
          );
        } else {
          toast.error(res.data?.message || "Failed to update priority");
        }
      } catch (err) {
        console.error(err);
        toast.error("Error updating priority");
      } finally {
        setStatusUpdatingId(null);
      }
    };

    const colorMap = {
      Low: "text-green-600",
      Medium: "text-orange-500",
      High: "text-red-500",
    };

    return (
      <Select
        value={val || "Low"}
        size="small"
        style={{ width: 110, height: "30px" }}
        options={PRIORITY_OPTS}
        onChange={handlePriorityChange}
        className={colorMap[val] || ""}
      />
    );
  },
},
    {
      title: "Est. Hours", dataIndex: "estimated_hours", key: "estimated_hours", width: 100, align: "right",
      render: (val, record) => {
        const isEditing = editingCell.id === record.Team_id && editingCell.field === "estimated_hours";
        if (isEditing) {
          return (
            <Input
              autoFocus
              size="small"
              type="number"
              min={0}
              defaultValue={val}
              style={{ width: 80, textAlign: "right" }}
              onBlur={(e) => handleCellSave(record, "estimated_hours", parseFloat(e.target.value) || 0)}
              onPressEnter={(e) => handleCellSave(record, "estimated_hours", parseFloat(e.target.value) || 0)}
              onClick={(e) => e.stopPropagation()}
            />
          );
        }
        return (
          <span
            className="text-[13px] text-gray-700 cursor-pointer hover:text-blue-600 hover:underline"
            onClick={() => setEditingCell({ id: record.Team_id, field: "estimated_hours" })}
            title="Click to edit"
          >
            {val != null ? Number(val).toFixed(2) : "0.00"}
          </span>
        );
      },
    },
    {
      title: "Worked Hrs", key: "worked_hours", width: 120, align: "center",
      render: (_, record) => {
        const members = (record.member_name || "").split(", ").map((n) => n.trim()).filter(Boolean);
        const workedList = record.member_worked_hrs || [];
        if (!members.length) return <span className="text-[13px] text-gray-400">-</span>;
        return (
          <div>
            {members.map((_, i) => {
              const hrs = workedList[i] || "00:00";
              const isZero = hrs === "00:00";
              return (
                <div key={i}>
                  <span className={`text-[12px] font-[500] ${isZero ? "text-gray-400" : "text-blue-600"}`}>{hrs}</span>
                  {i < members.length - 1 && <div style={{ borderBottom: "1px dashed #e0e0e0", margin: "3px 0" }} />}
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      title: "Balance Hrs", dataIndex: "balance_hours", key: "balance_hours", width: 110, align: "right",
      render: (val) => (
        <span className={`text-[13px] font-[500] ${val < 0 ? "text-red-500" : val === 0 ? "text-green-600" : "text-orange-500"}`}>
          {val != null ? Number(val).toFixed(2) : "0.00"}
        </span>
      ),
    },
    {
      title: "Action", key: "action", width: 70, align: "center", fixed: "right",
      render: (_, record) => (
        <FaTrashAlt onClick={() => openDeleteModal(record)} className="text-[14px] text-[#ff4d4d] cursor-pointer mx-auto" />
      ),
    },
  ], [data, editingCell, membersList, activeMembersList, addMemberPopoverId, newMemberIds, addMemberLoadingId]); // ← KEY FIX: editingCell in deps

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div>
      <ToastContainer />
      {contextHolder}

      <Spin
        spinning={
          statusUpdatingId !== null ||
          deleteLoading ||
          startDateUpdatingId !== null ||
          endDateUpdatingId !== null ||
          estHrsUpdatingId !== null ||
          taskNameUpdatingId !== null  // ← ADD
        }
        tip={
          deleteLoading ? "Deleting task..." :
            statusUpdatingId !== null ? "Updating status..." :
              startDateUpdatingId !== null ? "Updating start date..." :
                endDateUpdatingId !== null ? "Updating end date..." :
                  estHrsUpdatingId !== null ? "Updating estimated hours..." :
                    taskNameUpdatingId !== null ? "Updating task name..." : ""  // ← ADD
        }
        size="large"
      >


        <div className="mx-auto px-0">

          <div className="mb-[5px]">
            <h2 className="page-heading relative bottom-[-10px]">TEAMS ACTIVITIES</h2>
          </div>

          {/* ── Summary Cards ── */}
          <div className="mt-6">
            <div className="flex flex-wrap gap-4">
              {[
                { label: "Total Tasks", count: totalCount, color: "text-blue-600", sub: selectedProject === "All" ? "All Projects" : selectedProject },
                { label: "In Progress", count: inProgressCount, color: "text-green-600", sub: "Status: In Progress" },
                { label: "Open Tasks", count: openCount, color: "text-yellow-500", sub: "Status: Open" },
                { label: "Completed Tasks", count: completedCount, color: "text-purple-600", sub: "Status: Completed" },
              ].map(({ label, count, color, sub }) => (
                <div key={label} className="flex-1 min-w-[160px] p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                  <div className="text-center">
                    <span className="text-[12px] font-[500] text-gray-600">{label}</span>
                    <div className={`text-[28px] font-[600] ${color} mt-2`}>
                      {countsLoading ? <Skeleton active paragraph={false} title={false} className="w-[60px] mx-auto" /> : count}
                    </div>
                    <span className="text-[11px] text-gray-500 block mt-1">{sub}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Filters ── */}
          <div className="flex justify-between items-end gap-[15px] mt-3 z-[9]">
            <div className="flex items-end gap-2 flex-wrap">

              <button onClick={() => { setIsModalOpen(true); form.resetFields(); }}
                className="bg-blue-500 text-white py-[5px] px-3 rounded flex items-center justify-center w-[155px] h-[32px]">
                <span className="mr-2"><i className="fa fa-plus-circle text-[16px] relative top-[1px]"></i></span>
                <span className="text-[13px]">Create a Task</span>
              </button>

              <div className="w-[130px] flex flex-col gap-[5px]">
                <label className="text-[13px] pl-2">Projects:</label>
                <Select value={selectedProject} onChange={(v) => setSelectedProject(v)} options={PROJECT_OPTIONS} placeholder="Select Project" />
              </div>

              <div className="w-[160px] flex flex-col gap-[5px]">
                <label className="text-[13px] pl-2">Member:</label>
                <Select value={selectedMember} onChange={(v) => setSelectedMember(v)} options={MEMBER_OPTIONS} placeholder="Select Member" />
              </div>

              <div className="w-[100px] flex flex-col gap-[5px]">
                <label className="text-[13px] pl-2">Status:</label>
                <Select value={selectedStatus} onChange={(v) => setSelectedStatus(v)} options={[{ label: "All", value: "All" }, ...STATUS_OPTS]} placeholder="Select Status" />
              </div>

              <div className="flex flex-col gap-[5px]">
                <label className="text-[13px] pl-2">Time Period:</label>
                <Select style={{ width: 140 }} value={selectedTimePeriod}
                  options={[{ label: "Day", value: "day" }, { label: "Weekly Report", value: "week" }, { label: "Monthly Report", value: "month" }, { label: "Yearly Report", value: "year" }]}
                  onChange={handleTimePeriodChange} />
              </div>

              <div className="flex flex-col gap-[5px]">
                <label className="text-[13px] pl-2">Date:</label>
                {selectedTimePeriod === "month" && (
                  <RangePicker className="w-[260px]" allowClear={false} value={selectedDates} picker="month" placeholder={["Start Month", "End Month"]} format="MMMM YYYY" onChange={handleDateChange} disabledDate={(c) => c && c > dayjs().endOf("month")} />
                )}
                {selectedTimePeriod === "year" && (
                  <RangePicker className="w-[200px]" allowClear={false} value={selectedDates} picker="year" placeholder={["Start Year", "End Year"]} format="YYYY" onChange={handleDateChange} disabledDate={(c) => c && c.year() > dayjs().year()} />
                )}
                {selectedTimePeriod === "day" && (
                  <RangePicker className="w-[250px]" allowClear={false} value={selectedDates} placeholder={["Start Date", "End Date"]} format="DD-MM-YYYY" onChange={handleDateChange} disabledDate={(c) => c && c > dayjs().endOf("day")} />
                )}
                {selectedTimePeriod === "week" && (
                  <Flatpickr
                    className="ant-select ant-select-outlined w-[180px] date-picker-client-report custom-date-picker-client-report"
                    value={selectedWeekDates}
                    options={{
                      mode: "range",
                      dateFormat: "D, M j",
                      onChange: handleWeekSelection,
                      // ← Remove the disable array entirely (no future date restriction)
                    }}
                    placeholder="Select Week"
                  />
                )}
              </div>

              <div className="flex flex-col gap-[5px]">
                <label className="text-[13px]">Search</label>
                <Input placeholder="Search by Task, Project, Member..." value={searchText} onChange={(e) => setSearchText(e.target.value)}
                  allowClear prefix={<i className="fa fa-search text-gray-400"></i>} className="h-[32px] w-[280px]" />
              </div>

            </div>
          </div>

          {/* ── Create Modal ── */}
          <AntdModal className="workitem-popup" open={isModalOpen} footer={null} onCancel={() => setIsModalOpen(false)} closable maskClosable={false} keyboard={false} width={1000}>
            <div className="bg-white rounded-lg">
              <h3 className="text-[14px] font-[600] mb-4 px-[22px] mx-[-22px]" style={{ borderBottom: "1px solid #ddd", paddingBottom: "15px" }}>Create a Task</h3>
              <Form form={form} onFinish={handleSubmit} layout="vertical">
                <div className="task-project-modal">
                  <div className="grid grid-cols-3 gap-x-4">

                    <Form.Item label="Ticket ID" name="ticket_id" className="col-span-1" rules={[{ required: true, message: "Please enter the ticket ID" }]}>
                      <Input className="h-10" placeholder="Enter ticket ID" />
                    </Form.Item>


                    <Form.Item label="Task Name" name="task_name" className="col-span-1" rules={[{ required: true, message: "Please enter the task name" }]}>
                      <Input className="h-10" placeholder="Enter task name" />

                    </Form.Item>


                    <Form.Item label="Project" name="project_id" rules={[{ required: true, message: "Please select a project" }]}>
                      <Select className="h-10" placeholder="Select project" allowClear options={MODAL_PROJECT_OPTIONS} onChange={handleProjectChange} onClear={() => setSelectedClientId(null)} />
                    </Form.Item>
                    <Form.Item label="Member Name" name="member_ids" rules={[{ required: true, message: "Please select at least one member" }]}>
                      <Select
                        className="h-10"
                        placeholder="Select member(s)"
                        allowClear
                        showSearch
                        mode="multiple"
                        optionFilterProp="label"
                        options={MODAL_MEMBER_OPTIONS}
                        maxTagCount={0}
                        maxTagPlaceholder={(omittedValues) =>
                          `${omittedValues.length} Employee${omittedValues.length !== 1 ? "s" : ""}`
                        }
                      />
                    </Form.Item>



                    <Form.Item label="Status" name="status" initialValue="Open" rules={[{ required: true, message: "Please select a status" }]}>
                      <Select className="h-10" placeholder="Select status" options={STATUS_OPTS} />
                    </Form.Item>
                    <Form.Item label="Billing Status" name="billing_status" initialValue="Billable">
                      <Select className="h-10" placeholder="Select billing status">
                        <Select.Option value="Billable">Billable</Select.Option>
                        <Select.Option value="Non-Billable">Non-Billable</Select.Option>
                      </Select>
                    </Form.Item>

                    <Form.Item label="Priority" name="priority" initialValue="Low" rules={[{ required: true, message: "Please select a priority" }]}>
  <Select className="h-10" placeholder="Select priority" options={PRIORITY_OPTS} />
</Form.Item>
                    <Form.Item label="Estimated Hours" name="estimated_hours">
                      <Input type="number" className="h-10" placeholder="Enter estimated hours" min={0} />
                    </Form.Item>
                    <Form.Item name="start_date" label="Start Date" rules={[{ required: true, message: "Please select a start date" }]}>
                      <DatePicker className="w-full h-10" format="DD-MM-YYYY" placeholder="Select start date" />
                    </Form.Item>
                    <Form.Item name="end_date" label="End Date" rules={[{ required: true, message: "Please select an end date" }]}>
                      <DatePicker className="w-full h-10" format="DD-MM-YYYY" placeholder="Select end date"
                        disabledDate={(current) => { const start = form.getFieldValue("start_date"); if (!start) return false; return current && current.isBefore(start, "day"); }} />
                    </Form.Item>
                  </div>
                  <div className="text-right mt-6">
                    <Button className="w-[100px]" type="default" onClick={() => setIsModalOpen(false)} style={{ marginRight: "8px" }}>Cancel</Button>
                    <Button className="w-[100px]" type="primary" htmlType="submit" loading={modalLoading}>{modalLoading ? "Creating..." : "Create"}</Button>
                  </div>
                </div>
              </Form>
            </div>
          </AntdModal>

          {/* ── Delete Modal ── */}
          <AntdModal
            title={<><ExclamationCircleFilled style={{ color: "orange", marginRight: "8px" }} />Confirm</>}
            open={deleteModal} onCancel={() => { setDeleteModal(false); setDeleteId(null); }}
            closable maskClosable={false} keyboard={false}
            footer={[
              <Button key="cancel" onClick={() => { setDeleteModal(false); setDeleteId(null); }}>Cancel</Button>,
              <Button key="confirm" type="primary" danger loading={modalLoading} onClick={confirmDelete}>Delete</Button>,
            ]}
          >
            <p>Are you sure you want to delete: <strong>"{deleteTeamName}"</strong>?</p>
            <p>This action cannot be undone.</p>
          </AntdModal>


          {/* ── Task Name Confirmation Modal ── */}
          <AntdModal
            title={
              <span>
                <ExclamationCircleFilled style={{ color: "#faad14", marginRight: "8px" }} />
                Confirm Task Name Update
              </span>
            }
            open={taskNameConfirm.visible}
            onCancel={() => {
              // Rollback temp optimistic value if user cancels
              setTaskNameConfirm({ visible: false, record: null, value: "" });
            }}
            closable
            maskClosable={false}
            keyboard={false}
            footer={[
              <Button
                key="cancel"
                onClick={() => setTaskNameConfirm({ visible: false, record: null, value: "" })}
              >
                Cancel
              </Button>,
              <Button
                key="confirm"
                type="primary"
                onClick={confirmTaskNameUpdate}
              >
                Yes, Update
              </Button>,
            ]}
          >
            <div className="py-2">
              <p className="text-[13px] text-gray-600 mb-3">Are you sure you want to update the task name?</p>
              <div className="bg-gray-50 rounded p-3 border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[12px] text-gray-500 w-[60px]">From:</span>
                  <span className="text-[13px] font-[500] text-red-500 line-through">
                    {taskNameConfirm.record?.task_name}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[12px] text-gray-500 w-[60px]">To:</span>
                  <span className="text-[13px] font-[600] text-green-600">
                    {taskNameConfirm.value}
                  </span>
                </div>
              </div>
            </div>
          </AntdModal>

          {/* ── Table ── */}
          <div className="mt-5 overflow-auto custom-table custom-teams-activity absolute w-[98%]">
            <Spin spinning={tableLoading} tip="Loading...">
              <Table
                columns={columns} dataSource={data} rowKey="Team_id"
                size="small" bordered scroll={{ x: "max-content", y: 400 }}
                sticky={{ offsetHeader: 0 }}

                pagination={{
                  pageSize: pageSize,
                  showSizeChanger: true,
                  pageSizeOptions: ["15", "25", "50", "100"],
                  showTotal: (t) => `Total ${t} records`,
                  onShowSizeChange: (current, size) => {
                    setPageSize(size);
                  },
                  onChange: (page, size) => {
                    setPageSize(size);
                  },
                }}


                locale={{ emptyText: "No records found" }}
                rowClassName={(_, index) => index % 2 === 0 ? "bg-white" : "bg-[#fafafa]"}
              />
            </Spin>
          </div>

        </div>
      </Spin>
    </div>
  );
}